import { PageStatusBatchProgressResponse, PageStatusResponse } from '@/declarations/models/PageStatusResponse';
import { Api } from '@/services/Api';
import {
  createEmptyPageStatusGrouped,
  createPageStatusResponseFromGrouped,
  mergePageStatusGrouped,
  sleep,
} from './pageStatusUtils';

interface CheckAllFrontPageStatusesOptions {
  batchSize?: number;
  intervalMs?: number;
  errorsOnly?: boolean;
  signal?: AbortSignal;
  onProgress?: (pageStatusResponse: PageStatusResponse, progressResponse: PageStatusBatchProgressResponse) => void;
}

export async function checkAllFrontPageStatusesInBatches({
  batchSize = 25,
  intervalMs = 2000,
  errorsOnly = true,
  signal,
  onProgress,
}: CheckAllFrontPageStatusesOptions = {}): Promise<PageStatusResponse> {
  let offset = 0;
  let accumulatedGrouped = createEmptyPageStatusGrouped();

  const startedAt = Date.now();

  while (true) {
    if (signal?.aborted) {
      throw new DOMException('Polling aborted', 'AbortError');
    }

    // eslint-disable-next-line no-await-in-loop
    const [batchResponse, error, source] = await Api.getAllFrontPageStatusesBatch(
      offset,
      batchSize,
      errorsOnly,
    ).fetch();

    if (error || !batchResponse) {
      console.error(error, source);

      throw new Error(typeof error === 'string' ? error : 'Could not check frontpage statuses');
    }

    accumulatedGrouped = mergePageStatusGrouped(accumulatedGrouped, batchResponse.result);

    const elapsedTimeSeconds = Math.round((Date.now() - startedAt) / 1000);

    const pageStatusResponse = createPageStatusResponseFromGrouped(
      accumulatedGrouped,
      batchResponse.total,
      batchResponse.checked,
      elapsedTimeSeconds,
      errorsOnly,
    );

    const progressResponse: PageStatusBatchProgressResponse = {
      ...batchResponse,
      result: accumulatedGrouped,
      progress_percent: batchResponse.total > 0 ? Math.round((batchResponse.checked / batchResponse.total) * 100) : 0,
    };

    onProgress?.(pageStatusResponse, progressResponse);

    if (batchResponse.next_offset === null) {
      return pageStatusResponse;
    }

    offset = batchResponse.next_offset;

    // eslint-disable-next-line no-await-in-loop
    await sleep(intervalMs, signal);
  }
}

export interface CheckSitePageStatusesOptions {
  siteId: number;
  batchSize?: number;
  intervalMs?: number;
  errorsOnly?: boolean;
  signal?: AbortSignal;
  onProgress?: (pageStatusResponse: PageStatusResponse, progressResponse: PageStatusBatchProgressResponse) => void;
}

export async function checkSitePageStatusesInBatches({
  siteId,
  batchSize = 100,
  intervalMs = 250,
  errorsOnly = false,
  signal,
  onProgress,
}: CheckSitePageStatusesOptions): Promise<PageStatusResponse> {
  let accumulatedGrouped = createEmptyPageStatusGrouped();

  const startedAt = Date.now();

  const checkBatch = async (offset: number): Promise<PageStatusResponse> => {
    if (signal?.aborted) {
      throw new DOMException('Status check aborted', 'AbortError');
    }

    const [batchResponse, error, source] = await Api.getAllPageStatusesBatch(
      siteId,
      offset,
      batchSize,
      errorsOnly,
    ).fetch();

    if (error || !batchResponse) {
      console.error(error, source);

      throw new Error(typeof error === 'string' ? error : 'Could not check site page statuses');
    }

    accumulatedGrouped = mergePageStatusGrouped(accumulatedGrouped, batchResponse.result);

    const elapsedTimeSeconds = Math.round((Date.now() - startedAt) / 1000);

    const pageStatusResponse = createPageStatusResponseFromGrouped(
      accumulatedGrouped,
      batchResponse.total,
      batchResponse.checked,
      elapsedTimeSeconds,
      errorsOnly,
    );

    const progressResponse: PageStatusBatchProgressResponse = {
      ...batchResponse,
      result: accumulatedGrouped,
      progress_percent: batchResponse.total > 0 ? Math.round((batchResponse.checked / batchResponse.total) * 100) : 0,
    };

    onProgress?.(pageStatusResponse, progressResponse);

    if (batchResponse.next_offset === null) {
      return pageStatusResponse;
    }

    await sleep(intervalMs, signal);

    return checkBatch(batchResponse.next_offset);
  };

  return checkBatch(0);
}
